import React, { useState, useEffect } from 'react';
import axios from 'axios';
import SalesDetailPage from './SalesDetailPage';
import { REFUNDS_COLUMNS, SORT_TYPES, getDefaultVisibleColumns, getDefaultHiddenColumns } from '../constants/refundsColumnsConfig';
import { compactCurrency } from '../utils/reportingUtils';
import { buildRefundsFilterGroups } from '../constants/refundsFiltersConfig';

const STATE_BADGES = {
  reviewing: { bg: 'var(--sl-bg-tertiary, #f2f4f7)', color: 'var(--sl-text-secondary, #344054)' },
  ach_processing: { bg: 'var(--sl-bg-brand-primary, #eff8ff)', color: 'var(--sl-fg-brand, #175cd3)' },
  succeeded: { bg: 'var(--sl-bg-success-primary, #ecfdf3)', color: 'var(--sl-text-success, #027a48)' },
  failed: { bg: 'var(--sl-bg-error-primary, #fef3f2)', color: 'var(--sl-text-error, #b42318)' },
};

const TYPE_LABELS = {
  'Single order': 'Single Order',
  'Multiple order': 'Multiple Order',
  'Multiple face value': 'Face Value',
};

const buildDetailApiFilters = (activeFilters) => {
  const params = {};
  if (activeFilters.state && activeFilters.state.length > 0) {
    params.state = activeFilters.state;
  }
  if (activeFilters.reason && activeFilters.reason.length > 0) {
    params.reason = activeFilters.reason;
  }
  if (activeFilters.refund_type && activeFilters.refund_type.length > 0) {
    params.refund_type = activeFilters.refund_type;
  }
  if (activeFilters.calendar_event_id && activeFilters.calendar_event_id.length > 0) {
    params.calendar_event_id = activeFilters.calendar_event_id;
  }
  if (activeFilters.calendar_classification && activeFilters.calendar_classification.length > 0) {
    params.calendar_classification = activeFilters.calendar_classification;
  }
  if (activeFilters.venue_ownership_id && activeFilters.venue_ownership_id.length > 0) {
    params.venue_ownership_id = activeFilters.venue_ownership_id;
  }
  return params;
};

const REFUNDS_CONFIG = {
  pageTitle: 'Refunds',
  pageSubtitle: 'Refund activity across your venues.',
  sidebarKey: 'finances_refunds',
  defaultPreset: 'year_to_date',
  dateModeOptions: [{ key: 'order_date', label: 'Refund Date' }],
  defaultSortRules: [{ field: 'created_at', direction: 'desc' }],

  columns: REFUNDS_COLUMNS,
  sortTypes: SORT_TYPES,
  getDefaultVisibleColumns,
  getDefaultHiddenColumns,
  localStorageKey: (teamId) => `reportingSalesRefundColumns_${teamId}`,
  firstColumnId: 'customer_name',

  kpiFromDetailResponse: true,
  salesDataKey: null,
  venueSeriesKey: null,
  detailEndpoint: (teamId) => `/teams/${teamId}/reporting/finances/refunds/data.json`,
  detailResponseKey: 'refunds',
  exportBasePath: (teamId) => `/teams/${teamId}/reporting/finances/refunds/export`,
  salesExportSection: 'refunds',
  detailErrorMessage: 'Failed to load refund data.',
  detailTableTitle: 'Refunds',
  emptyMessage: 'No refunds found.',
  searchPlaceholder: 'Search refunds...',
  paginationLabel: 'refunds',
  itemLabel: (count) => `refund${count !== 1 ? 's' : ''}`,

  buildDetailApiFilters,

  buildKpiCards: (current, prior) => [
    { label: 'Total Refunds', value: (current.total_refunds || 0).toLocaleString(), current: current.total_refunds || 0, prior: prior.total_refunds || 0 },
    { label: 'Total Refunded', value: compactCurrency(current.total_refunded || 0), current: current.total_refunded || 0, prior: prior.total_refunded || 0 },
    { label: 'Avg Refund', value: compactCurrency(current.avg_refund_amount || 0), current: current.avg_refund_amount || 0, prior: prior.avg_refund_amount || 0 },
    { label: 'Pending', value: (current.pending_count || 0).toLocaleString(), current: current.pending_count || 0, prior: prior.pending_count || 0 },
  ],
  kpiGridColumns: 4,

  overTimeKey: null,
  topCharts: [
    { title: 'Top Refund Reasons', dataKey: 'by_reason', barDataKey: 'count', nameKey: 'name', barColor: 'var(--sl-text-error, #b42318)', isCurrency: false, tooltipLabel: 'Refunds' },
    { title: 'Top Events by Refunds', dataKey: 'by_event', barDataKey: 'count', nameKey: 'name', barColor: '#6A4C93', isCurrency: false, tooltipLabel: 'Refunds' },
  ],
  groupByOptions: null,
  groupByDefault: null,

  renderCellValue: (col, refund, { formatEventDateTime, currencyFormatter: cf }) => {
    switch (col.id) {
      case 'customer_name': return refund.customer_name || '\u2014';
      case 'customer_email': return refund.customer_email || '\u2014';
      case 'event_name': return refund.event_name || '\u2014';
      case 'venue_name': return refund.venue_name || '\u2014';
      case 'reason': return refund.reason || '\u2014';
      case 'amount': return cf.format(refund.amount || 0);
      case 'state': {
        const badge = STATE_BADGES[refund.state] || STATE_BADGES.reviewing;
        const label = refund.state ? refund.state.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) : '\u2014';
        return <span className="badge" style={{ background: badge.bg, color: badge.color, fontSize: 12 }}>{label}</span>;
      }
      case 'refund_type': {
        const cleaned = TYPE_LABELS[refund.refund_type] || refund.refund_type || '\u2014';
        return cleaned;
      }
      case 'created_at': return <span className="text-nowrap">{formatEventDateTime(refund.created_at)}</span>;
      case 'confirmed_at': return <span className="text-nowrap">{refund.confirmed_at ? formatEventDateTime(refund.confirmed_at) : '\u2014'}</span>;
      default: return '\u2014';
    }
  },

  filterTitle: 'Filter Refunds',
  filterSubtitle: 'Apply filters to narrow refund data.',
};

function ReportingFinancesRefundsPage({ team, csrfToken, ...props }) {
  const [filterOptions, setFilterOptions] = useState({});

  useEffect(() => {
    if (!team?.id) return;
    if (csrfToken) axios.defaults.headers.common['X-CSRF-TOKEN'] = csrfToken;
    axios.get(`/teams/${team.id}/reporting/finances/refunds/filter_options.json`)
      .then((res) => setFilterOptions(res.data || {}))
      .catch(() => {});
  }, [team?.id, csrfToken]);

  const configWithFilters = {
    ...REFUNDS_CONFIG,
    buildFilterGroups: () => buildRefundsFilterGroups(filterOptions),
  };
  return <SalesDetailPage {...props} team={team} csrfToken={csrfToken} config={configWithFilters} />;
}

export default ReportingFinancesRefundsPage;
